/* eslint-disable no-unused-expressions */
/*
 * Copyright (c) 2023 IQVIA Corp.  All Rights Reserved
 */

import MaskedInput from 'react-input-mask';
import { Box, MenuItem } from '@mui/material';
import { Input } from 'ihp-components/components/v2/Input';
import { FormCheckbox } from 'ihp-components/components/v2/Input/Checkbox';
import { ReactComponent as LocationIcon } from 'images/personalinformation/location-filled.svg';
import { ReactComponent as Calendar } from 'ihp-components/components/v2/assets/svg/calendar.svg';
import NotificationPreference from 'components/NotificationPreference';
import StyledRadioTypeButton from 'components/Buttons/StyledRadioTypeButton';

import {
  useValidateZipCode,
  useValidateEmail,
  useValidateName,
  validateDate,
  useRegexValidatePhone,
} from './formValidation';
import MultiselectCheckbox from '../components/MultiselectCheckbox';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

export const ACCOUNT_HOLDER_PROFILE = 'accountHolderProfile';
export const PARTICIPANT_PROFILE = 'participantProfile';
export const PERSONAL_INFORMATION = 'personalInformation';

// unique identifiers for field types ...
export const input = 'input';
export const checkbox = 'checkbox';
export const selection = 'selection';
export const date = 'date';
export const custom = 'custom';
export const switches = 'switches';
export const multiselect = 'multiselect';

// unique identifiers for field types ...
export const first_name = 'first_name';
export const last_name = 'last_name';
export const middle_name = 'middle_name';
export const no_middle_name = 'no_middle_name';
export const preferred_name = 'preferred_name';
export const street_address = 'street';
export const country = 'country';
export const state = 'state';
export const city = 'city';
export const postal_code = 'postal_code';
export const date_of_birth = 'date_of_birth';
export const year_of_birth = 'year_of_birth';
export const email = 'email';
export const phone = 'phone';
export const notification_preferences = 'notification_preferences';

export const birth_first_name = 'birth_first_name';
export const birth_middle_name = 'birth_middle_name';
export const birth_last_name = 'birth_last_name';
export const birth_no_middle_name = 'birth_no_middle_name';

export const sex_at_birth = 'sex_at_birth';
export const city_of_birth = 'city_of_birth';
export const country_of_birth = 'country_of_birth';
export const ethnicity = 'ethnicity';
export const gender = 'gender';
export const educational_level = 'educational_level';
export const marital_status = 'marital_status';
export const date_of_death = 'date_of_death';
export const different_name_at_birth = 'different_name_at_birth';
export const participant_deceased = 'participant_deceased';

const DateWrapper = (props) => {
  const { showOnlyYear, label, placeholder, ...rest } = props;
  const views = showOnlyYear && ['year'];
  return (
    <Input
      type='datePicker'
      label={label}
      placeholder={placeholder}
      datePickerIcon={() => <Calendar />}
      disableFuture
      fullWidth={true}
      views={views}
      {...rest}
    />
  );
};

const SelectWrapper = (props) => {
  const { optionsList, label, ...rest } = props;
  return (
    <Input type='select' fieldLabel={label} label={label} {...rest}>
      {optionsList?.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Input>
  );
};

export const notificationMessages = {
  error: 'Something went wrong, Please try again',
  updateSuccess: 'Details Updated Successfully !',
  updateInfo: 'No changes, Please edit a field to save',
};

export const FIELD_TYPE_MAP = {
  [input]: Input,
  [checkbox]: FormCheckbox,
  [selection]: SelectWrapper,
  [date]: DateWrapper,
  [multiselect]: MultiselectCheckbox,
};

export const CUSTOM_FIELD_TYPE_MAP = {
  [notification_preferences]: NotificationPreference,
};

// when field.type=custom
export const FIELD_NAME_MAP = {
  [notification_preferences]: <div>Custom type component</div>,
};

export const FIELD_COL_SPAN = {
  [first_name]: 4,
  [middle_name]: 4,
  [last_name]: 4,
  [no_middle_name]: 12,
  [preferred_name]: 12,
  [street_address]: 12,
  [country]: 6,
  [state]: 6,
  [city]: 6,
  [postal_code]: 6,
  [date_of_birth]: 12,
  [email]: 12,
  [phone]: 12,
  [notification_preferences]: 12,
  [birth_first_name]: 4,
  [birth_middle_name]: 4,
  [birth_last_name]: 4,
  [birth_no_middle_name]: 12,
  [sex_at_birth]: 6,
  [city_of_birth]: 6,
  [country_of_birth]: 6,
};

export const ACCOUNT_HOLDER_PROFILE_FIELDS_API_MAP = {
  [first_name]: 'firstName',
  [middle_name]: 'middleName',
  [last_name]: 'lastName',
  [street_address]: 'street',
  [country]: country,
  [state]: state,
  [city]: city,
  [postal_code]: 'zipCode',
  [date_of_birth]: 'dateOfBirth',
};

export const CONTACT_INFO_FIELDS_API_MAP = {
  [email]: email,
  [phone]: phone,
  [notification_preferences]: 'notificationPreferences',
};

export const FIELDS_API_MAP = {
  ...ACCOUNT_HOLDER_PROFILE_FIELDS_API_MAP,
  ...CONTACT_INFO_FIELDS_API_MAP,
};

export const LANG = 'en-us';

const Countries = {
  US: 'US',
  CA: 'CA',
};

const getPostalCodeInputMaskVal = (country) => {
  let maskval = null;
  if (country === Countries.CA) {
    maskval = 'aaa 999';
  }
  if (country === Countries.US) {
    maskval = '99999';
  }
  return maskval;
};

/**
 * Remove duplicated fields (fields with same `name`)
 */

const getUniqueFields = (fields = []) => {
  const uniques = fields.filter(
    (obj, index) =>
      fields.findIndex((item) => item?.name === obj?.name) === index
  );

  return uniques;
};

export function getBoolPropertyValue(displayProperty) {
  if (typeof displayProperty === 'object') {
    return displayProperty.DTP;
  }
  return displayProperty;
}

export const generatePersonInformationFieldsMap = (fields, country) => {
  let fieldsMap = [];
  fields?.forEach((field, index) => {
    if (getBoolPropertyValue(field?.display)) {
      const i = field?.order ? field?.order - 1 : index;
      const placeholder = field?.label?.[LANG];
      const label = getBoolPropertyValue(field?.required)
        ? field?.label?.[LANG]
        : `${field?.label?.[LANG]} (Optional)`;
      const name = field?.key;
      const entity = 'field';
      const type = field?.type ?? input;
      const fieldMap = {
        name,
        entity,
        label,
        placeholder,
        show: getBoolPropertyValue(field?.display),
        order: i,
        Component: FIELD_TYPE_MAP[type] ?? FIELD_NAME_MAP[field?.key],
        type: type !== 'custom' ? type : CUSTOM_FIELD_TYPE_MAP[field?.key],
        required: getBoolPropertyValue(field?.required),
        disabled: !getBoolPropertyValue(field?.editable),
        fullWidth: true,
        colSpan: FIELD_COL_SPAN[field?.key],
        validation: {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          ...(field?.validation
            ? {
                pattern: {
                  value: new RegExp(field?.validation),
                  message: 'Enter valid input',
                },
              }
            : {}),
        },
      };

      if (field?.key === 'street') {
        fieldMap.helperText = 'IMPORTANT: Please provide the address where you would like to receive your DNA Collection Kit';
      }

      // type specific overrides ...
      if (type === checkbox) {
        fieldMap.items = [
          {
            id: 1,
            label: field?.label?.[LANG],
            value: name,
          },
        ];
        fieldMap.marginTop = '10px';
      }
      if (type === selection) {
        fieldMap.options = [];
      }
      if (field?.key === city) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : false),
          validate: useValidateName(),
        };
      }
      if (field?.key === email) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          validate: useValidateEmail(),
        };
      }
      if (field?.key === phone) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          validate: useRegexValidatePhone(phone, country),
        };
        fieldMap.placeholder = 'Enter phone';
      }
      if (field?.key === postal_code) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          validate: useValidateZipCode(country),
        };
      }
      if (
        field?.key === date_of_birth ||
        field?.key === year_of_birth ||
        field?.key === date_of_death
      ) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          validate: validateDate,
        };
      }
      fieldsMap[i] = fieldMap;
    }
  });

  /**
   * Remove duplicated fields (fields with same `name`)
   */

  fieldsMap = getUniqueFields(fieldsMap);

  /**
   * Sort fields based on order
   */

  fieldsMap = fieldsMap.sort((a, b) => {
    return a.order - b.order;
  });

  return fieldsMap;
};

export const generateFieldsMap = (fields, country, isParentReadOnly) => {
  let fieldsMap = [];
  fields?.forEach((field, index) => {
    if (getBoolPropertyValue(field?.display)) {
      const i = field?.order ? field?.order - 1 : index;
      const placeholder = field?.label?.[LANG];
      const label = field?.label?.[LANG];
      const name = field?.key;
      const entity = 'field';
      const type = field?.type ?? input;
      const fieldMap = {
        name,
        entity,
        label,
        placeholder,
        show: getBoolPropertyValue(field?.display),
        order: i,
        Component:
          FIELD_TYPE_MAP[type] || CUSTOM_FIELD_TYPE_MAP[field?.key] || Input,
        type: type !== 'custom' ? type : CUSTOM_FIELD_TYPE_MAP[field?.key],
        required: getBoolPropertyValue(field?.required),
        disabled: isParentReadOnly
          ? true
          : !getBoolPropertyValue(field?.editable),
        fullWidth: true,
        colSpan: FIELD_COL_SPAN[field?.key],
        validation: {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : false),
          ...(field?.validation
            ? {
                pattern: {
                  value: new RegExp(field?.validation),
                  message: 'Enter valid input',
                },
              }
            : false),
        },
      };

      // type specific overrides ...
      if (type === checkbox) {
        fieldMap.items = [
          {
            id: 1,
            label: field?.label?.[LANG],
            value: name,
          },
        ];
        fieldMap.marginTop = '10px';
      }
      if (type === selection) {
        fieldMap.options = [];
      }
      if (field?.key === city || field?.key === city_of_birth) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : false),
          validate: useValidateName(),
        };
      }
      if (field?.key === email) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : false),
          validate: useValidateEmail(),
        };
      }
      if (field?.key === phone) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          validate: useRegexValidatePhone(phone),
        };
        fieldMap.placeholder = 'Enter phone';
      }
      if (field?.key === postal_code) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : false),
          validate: useValidateZipCode(country),
        };
      }
      if (
        field?.key === date_of_birth ||
        field?.key === year_of_birth ||
        field?.key === date_of_death
      ) {
        fieldMap.validation = {
          ...(getBoolPropertyValue(field?.required)
            ? { required: 'This field is required.' }
            : {}),
          validate: validateDate,
        };
      }

      fieldsMap[i] = fieldMap;
    }
  });

  /**
   * Remove duplicated fields (fields with same `name`)
   */

  fieldsMap = getUniqueFields(fieldsMap);

  /**
   * Sort fields based on order
   */

  fieldsMap = fieldsMap.sort((a, b) => {
    return a.order - b.order;
  });

  return fieldsMap;
};

export const generateGroupedFields = (groups, country, isParentReadOnly) => {
  let formFeildsMap = [];
  if (groups?.length === 0) {
    return formFeildsMap;
  }
  groups?.forEach((group, index) => {
    if (getBoolPropertyValue(group?.display)) {
      const isGroupReadOnly = isParentReadOnly
        ? true
        : !getBoolPropertyValue(group?.editable);
      const groupEntity = {
        name: group?.label?.[LANG],
        entity: 'group',
        fields: [],
        isGroupReadOnly,
      };
      const fields = generateFieldsMap(
        group?.fields,
        country,
        isParentReadOnly
      );
      groupEntity.fields = fields;
      const i = group?.order ? group?.order - 1 : index;
      formFeildsMap[i] = groupEntity;
    }
  });
  return formFeildsMap;
};

export const getAccountHolderProfileInfo = (config) => {
  const { meta, tabs } = config?.[ACCOUNT_HOLDER_PROFILE];
  return {
    meta,
    tabs,
  };
};

const getParticipantProfileInfo = (config) => {
  const { meta, tabs } = config?.[PARTICIPANT_PROFILE];
  return {
    meta,
    tabs,
  };
};

export const getPersonInformation = (config) => {
  let personalInformation = {};
  if (config) {
    personalInformation = config?.[PERSONAL_INFORMATION] || {};
  }
  return personalInformation;
};

export const getAccountHolderProfileTabInfo = (config, tabName, country) => {
  let meta = {};
  let groups = {};
  let isTabReadOnly = false;
  let isTabHasGroup = false;
  let fieldsMap = [];
  let fields = [];

  if (config && config?.[ACCOUNT_HOLDER_PROFILE]) {
    meta = getAccountHolderProfileInfo(config)?.tabs?.filter(
      (tab) => tab?.meta?.label?.[LANG] === tabName
    )?.[0]?.meta;
    const tabInfo = getAccountHolderProfileInfo(config)?.tabs?.filter(
      (tab) => tab?.meta?.label?.[LANG] === tabName
    )?.[0];

    isTabReadOnly = !getBoolPropertyValue(meta?.editable);

    if (tabInfo.groups) {
      isTabHasGroup = true;
      groups = tabInfo.groups;
      const filterGroupsData = groups?.filter((item) =>
        getBoolPropertyValue(item?.display)
      );
      fieldsMap = generateGroupedFields(
        filterGroupsData,
        country,
        isTabReadOnly
      );
    }

    if (tabInfo.fields) {
      fields = tabInfo.fields;
      fieldsMap = generateFieldsMap(fields, country, isTabReadOnly);
    }
  }
  return {
    meta,
    groups,
    isTabReadOnly,
    isTabHasGroup,
    fieldsMap,
  };
};

export const getParticipantProfileTabInfo = (config, tabName, country) => {
  let meta = {};
  let groups = {};
  let isTabReadOnly = false;
  let isTabHasGroup = false;
  let fieldsMap = [];
  let fields = [];
  if (config && config?.[PARTICIPANT_PROFILE]) {
    meta = getParticipantProfileInfo(config)?.tabs?.filter(
      (tab) => tab?.meta?.label?.[LANG] === tabName
    )?.[0]?.meta;
    const tabInfo = getParticipantProfileInfo(config)?.tabs?.filter(
      (tab) => tab?.meta?.label?.[LANG] === tabName
    )?.[0];
    isTabReadOnly = !getBoolPropertyValue(meta?.editable);

    if (tabInfo.groups) {
      isTabHasGroup = true;
      groups = tabInfo.groups;
      const filterGroupsData = groups?.filter((item) =>
        getBoolPropertyValue(item?.display)
      );
      fieldsMap = generateGroupedFields(
        filterGroupsData,
        country,
        isTabReadOnly
      );
    }

    if (tabInfo.fields) {
      fields = tabInfo.fields;
      fieldsMap = generateFieldsMap(fields, country, isTabReadOnly);
    }
  }
  return {
    meta,
    groups,
    isTabReadOnly,
    isTabHasGroup,
    fieldsMap,
  };
};

export const renderPhoneField = (fieldData) => {
  const { name, label, placeholder, country, Component, endIcon, ...rest } = fieldData;

  const handleKeyDown = (e) => {
    const { key, ctrlKey, metaKey, shiftKey, target } = e;
    const { value, selectionStart } = target;

    if (
        ((ctrlKey || metaKey) && key === 'a') ||
        ((ctrlKey || metaKey) && key === 'x') ||
        (shiftKey && key === 'ArrowLeft') ||
        (shiftKey && key === 'ArrowRight')
    ) {
      e.preventDefault();
    }

    if (
        (key === 'Backspace' || e.keyCode === 8) &&
        value.length == 1 &&
        value[0] === '+'
    ) {
      e.preventDefault();
    }
  };

  const maskedInput = <MaskedInput placeholder={placeholder} mask='+1 (999) 999-9999' {...rest}>
    {(inputProps) => (
        <Input
            type='text'
            label={label}
            name={name}
            placeholder={placeholder}
            fullWidth={true}
            {...inputProps}
        />
    )}
  </MaskedInput>;

  const unmaskedInput = <Input
      type='text'
      label={label}
      name={name}
      placeholder={placeholder}
      fullWidth={true}
      endIcon={endIcon}
      onDoubleClick={(e) => e.preventDefault()}
      style={{ userSelect: 'none' }}
      onKeyDown={handleKeyDown}
      {...rest}
  />;

  return ['US', 'CA'].includes(country) ? maskedInput : unmaskedInput;
};

export const renderPostalcode = (fieldData) => {
  const { name, label, placeholder, Component, ...rest } = fieldData;
  return (
    <Input
      type='text'
      label={label}
      name={name}
      placeholder={placeholder}
      fullWidth={true}
      endIcon={<LocationIcon color='#D9D9D9' />}
      {...rest}
    />
  );
};

export const getSortedTabs = (tabs) => {
  return tabs
    ?.filter((tab) => getBoolPropertyValue(tab.meta.display))
    .sort((a, b) => a.meta.order - b.meta.order);
};

export const FIELD_TYPE_RENDERERS_MAP = {
  [phone]: renderPhoneField,
  [postal_code]: renderPostalcode,
  [different_name_at_birth]: StyledRadioTypeButton,
  [participant_deceased]: StyledRadioTypeButton,
};

export const ACCORDION_ICONS = {
  expandIcon: <Box color="primary.avgreen"><AddIcon /></Box>,
  collapseIcon: <Box color="primary.avgreen"><RemoveIcon /></Box>
};